/**
 * Derive media-type flags from a MediaAsset or a raw mime_type string.
 *
 * The backend stores `mime_type` on MediaAssetLocation (e.g. 'video/mp4',
 * 'image/jpeg', 'audio/mpeg'). Per the ND-1724 decision, there is no
 * explicit media_type enum on MediaAsset; consumers derive a discrete type
 * here.
 *
 * Missing / unrecognized mime_types return `type: 'unknown'` with every
 * concrete flag (isVideo / isImage / isAudio) set to false. Consumers that
 * need legacy "fall back to video" behavior for null-mime data should gate
 * on `!isImage && !isAudio` rather than on `isVideo` — that preserves the
 * historical assumption (everything was video) without misclassifying a new
 * still-image ingest whose mime_type has landed but isn't in the map.
 *
 * Usage in Options API components:
 *   computed: {
 *     isVideo() { return useMediaType(this.asset).isVideo },
 *     isImage() { return useMediaType(this.asset).isImage },
 *     isAudio() { return useMediaType(this.asset).isAudio },
 *   }
 */

// Extension → discrete media type. Used as a fallback when mime_type is
// missing, which happens for legacy assets uploaded before the backend
// persisted mime_type and for any asset whose upload path did not forward
// file.type to the API. Kept narrow: only the extensions the VMS pipeline
// accepts.
const EXTENSION_TYPE_MAP = {
  mp4: 'video',
  m4v: 'video',
  mov: 'video',
  wmv: 'video',
  mxf: 'video',
  mkv: 'video',
  h264: 'video',
  m2ts: 'video',
  webm: 'video',
  mpg: 'video',
  mpeg: 'video',
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  tiff: 'image',
  webp: 'image',
  mp3: 'audio',
  wav: 'audio',
  m4a: 'audio',
  aac: 'audio',
  flac: 'audio',
  ogg: 'audio',
  opus: 'audio',
  wma: 'audio'
}

function typeFromExtension(url) {
  if (typeof url !== 'string' || url.length === 0) return null
  // Strip query string and fragment before inspecting the extension.
  const cleanUrl = url.split('?')[0].split('#')[0]
  const match = /\.([^./\\]+)$/.exec(cleanUrl)
  if (!match) return null
  return EXTENSION_TYPE_MAP[match[1].toLowerCase()] || null
}

function shape(mimeType, type) {
  return {
    mimeType,
    type,
    isVideo: type === 'video',
    isImage: type === 'image',
    isAudio: type === 'audio',
    isUnknown: type === 'unknown'
  }
}

/**
 * Returns true if the asset should be treated as video. Assets with no
 * mime_type (legacy uploads) are assumed to be video — the historical default
 * for this app — unless the extension or mime_type positively identifies them
 * as image or audio.
 */
export function isVideo(asset) {
  const { isVideo: knownVideo, isImage, isAudio } = useMediaType(asset)
  return knownVideo || (!isImage && !isAudio)
}

export function useMediaType(assetOrMime) {
  const isAsset = typeof assetOrMime === 'object' && assetOrMime !== null
  const mime = typeof assetOrMime === 'string' ? assetOrMime : assetOrMime?.mime_type

  if (typeof mime === 'string' && mime.length > 0) {
    let type = 'unknown'
    if (mime.startsWith('image/')) type = 'image'
    else if (mime.startsWith('audio/')) type = 'audio'
    else if (mime.startsWith('video/')) type = 'video'
    // Legacy: an MXF file from some NRCS systems comes across as
    // application/mxf. Treat it as video.
    else if (mime === 'application/mxf') type = 'video'
    return shape(mime, type)
  }

  // mime_type missing — fall back to the URL extension if the asset gives
  // us one. Legacy assets persisted before mime_type was required, and the
  // former browser upload path, both end up here.
  if (isAsset) {
    const url = assetOrMime.original || assetOrMime.thumbnail || assetOrMime.proxy || assetOrMime.mezzanine
    const type = typeFromExtension(url)
    if (type) return shape(null, type)
  }

  return shape(null, 'unknown')
}
